"use client";

import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { Sun, Moon, Monitor, HardDrive, Trash2 } from "lucide-react";
import { useUserSettingsStore } from "@/lib/stores/user-settings";
import { useStorageEstimate } from "@/lib/hooks/use-storage-estimate";
import { ColorMap, COLOR_MAP_VALUES, getColorMapLabel } from "@/lib/shaders/colormaps";
import { getColormapGradient } from "@/lib/utils/colormap-gradients";
import { useThemeIsDark } from "@/lib/hooks/3d/use-theme-viewer-colors";
import { Select, SelectItem, SelectContent, SelectTrigger, SelectValue } from "@/ui/select";
import { Switch } from "@/ui/switch";
import { Slider } from "@/ui/slider";
import { Card, CardContent } from "@/ui/card";
import { Input } from "@/ui/input";
import { Label } from "@/ui/label";
import { Text } from "@/ui/text";
import { Heading } from "@/ui/heading";
import { Separator } from "@/ui/separator";
import { Button } from "@/ui/button";
import { SegmentedControl } from "@/ui/segmented-control";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function SettingsContent() {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setMounted(true), []);
  const isDark = useThemeIsDark();

  const {
    usageBytes,
    quotaBytes,
    loading: storageLoading,
    clearing,
    clearCache,
  } = useStorageEstimate();

  const colormap = useUserSettingsStore((s) => s.colormap);
  const meshColor = useUserSettingsStore((s) => s.meshColor);
  const wireframeEnabled = useUserSettingsStore((s) => s.wireframeEnabled);
  const useLitMaterial = useUserSettingsStore((s) => s.useLitMaterial);
  const meshRoughness = useUserSettingsStore((s) => s.meshRoughness);
  const meshMetalness = useUserSettingsStore((s) => s.meshMetalness);
  const meshOpacity = useUserSettingsStore((s) => s.meshOpacity);
  const setColormap = useUserSettingsStore((s) => s.setColormap);
  const setMeshColor = useUserSettingsStore((s) => s.setMeshColor);
  const setWireframeEnabled = useUserSettingsStore((s) => s.setWireframeEnabled);
  const setUseLitMaterial = useUserSettingsStore((s) => s.setUseLitMaterial);
  const setMeshRoughness = useUserSettingsStore((s) => s.setMeshRoughness);
  const setMeshMetalness = useUserSettingsStore((s) => s.setMeshMetalness);
  const setMeshOpacity = useUserSettingsStore((s) => s.setMeshOpacity);

  return (
    <div className="grid gap-6">
      <div>
        <Heading>Appearance</Heading>
        <div className="grid gap-2">
          <Label>Theme</Label>
          {mounted ? (
            <SegmentedControl
              value={theme ?? "system"}
              onValueChange={(v) => setTheme(v)}
              options={[
                {
                  value: "light",
                  label: (
                    <>
                      <Sun className="size-3.5" /> Light
                    </>
                  ),
                },
                {
                  value: "dark",
                  label: (
                    <>
                      <Moon className="size-3.5" /> Dark
                    </>
                  ),
                },
                {
                  value: "system",
                  label: (
                    <>
                      <Monitor className="size-3.5" /> System
                    </>
                  ),
                },
              ]}
            />
          ) : (
            <div className="h-9" />
          )}
        </div>
      </div>

      <Separator />

      <div>
        <Heading>Data Visualization</Heading>
        <div className="grid gap-2">
          <Label htmlFor="colormap-select">Colormap</Label>
          <Select
            name="colormap"
            value={colormap.toString()}
            onValueChange={(val) => setColormap(Number(val) as ColorMap)}
          >
            <SelectTrigger id="colormap-select">
              <SelectValue placeholder="Select a colormap..." />
            </SelectTrigger>
            <SelectContent>
              {COLOR_MAP_VALUES.map((value) => {
                const gradient = getColormapGradient(value, isDark);
                const label = getColorMapLabel(value);
                return (
                  <SelectItem key={value} value={value.toString()}>
                    <span
                      style={{
                        display: "inline-block",
                        width: 48,
                        height: 12,
                        borderRadius: 4,
                        marginRight: 8,
                        verticalAlign: "middle",
                        background: gradient,
                      }}
                    />
                    {label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Separator />

      <div>
        <Heading>Mesh Appearance</Heading>
        <div className="grid gap-6">
          <div className="grid gap-2">
            <Label htmlFor="mesh-color-input">Mesh Color</Label>
            <div className="flex items-center gap-3">
              <Input
                id="mesh-color-input"
                type="color"
                value={meshColor}
                onChange={(event) => setMeshColor(event.target.value)}
                aria-label="Mesh color"
                className="h-10 w-16 p-1"
              />
              <Text size="sm" color="muted">
                {meshColor.toUpperCase()}
              </Text>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="opacity-slider">Opacity</Label>
            <div className="flex items-center gap-3">
              <Slider
                id="opacity-slider"
                min={0}
                max={1}
                step={0.01}
                value={[meshOpacity]}
                onValueChange={(val) => setMeshOpacity(val[0])}
                className="flex-1"
              />
              <Text size="sm" color="muted" className="w-12 text-right">
                {meshOpacity.toFixed(2)}
              </Text>
            </div>
          </div>

          <Card>
            <CardContent className="grid gap-4 pt-4">
              <div className="grid gap-2">
                <Label>Mesh Material</Label>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={useLitMaterial}
                    onCheckedChange={(useLit) => {
                      setUseLitMaterial(useLit);
                      if (!useLit) setWireframeEnabled(true);
                    }}
                    aria-label="Use lit material"
                  />
                  <Text size="sm" color="muted">
                    {useLitMaterial ? "Lit" : "Unlit"}
                  </Text>
                </div>
              </div>

              {useLitMaterial && (
                <>
                  <div className="grid gap-2">
                    <Label>Wireframe</Label>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={wireframeEnabled}
                        onCheckedChange={setWireframeEnabled}
                        aria-label="Show mesh wireframe"
                      />
                      <Text size="sm" color="muted">
                        {wireframeEnabled ? "On" : "Off"}
                      </Text>
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="roughness-slider">Roughness</Label>
                    <div className="flex items-center gap-3">
                      <Slider
                        id="roughness-slider"
                        min={0}
                        max={1}
                        step={0.01}
                        value={[meshRoughness]}
                        onValueChange={(val) => setMeshRoughness(val[0])}
                        className="flex-1"
                      />
                      <Text size="sm" color="muted" className="w-12 text-right">
                        {meshRoughness.toFixed(2)}
                      </Text>
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="metalness-slider">Metalness</Label>
                    <div className="flex items-center gap-3">
                      <Slider
                        id="metalness-slider"
                        min={0}
                        max={1}
                        step={0.01}
                        value={[meshMetalness]}
                        onValueChange={(val) => setMeshMetalness(val[0])}
                        className="flex-1"
                      />
                      <Text size="sm" color="muted" className="w-12 text-right">
                        {meshMetalness.toFixed(2)}
                      </Text>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Separator />

      <div>
        <Heading>Storage</Heading>
        <div className="grid gap-4">
          <div className="flex items-center gap-3">
            <HardDrive className="size-4 text-muted-foreground" />
            <div className="flex-1">
              {storageLoading ? (
                <Text size="sm" color="muted">
                  Calculating…
                </Text>
              ) : usageBytes !== null ? (
                <div className="grid gap-1">
                  <Text size="sm">{formatBytes(usageBytes)} used</Text>
                  {quotaBytes !== null && (
                    <>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary transition-all"
                          style={{
                            width: `${Math.min(100, (usageBytes / quotaBytes) * 100).toFixed(1)}%`,
                          }}
                        />
                      </div>
                      <Text size="sm" color="muted">
                        {formatBytes(quotaBytes)} available
                      </Text>
                    </>
                  )}
                </div>
              ) : (
                <Text size="sm" color="muted">
                  Storage estimate unavailable
                </Text>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Text size="sm">Cached 3D files</Text>
              <Text size="sm" color="muted">
                STL and GLB files stored for offline use
              </Text>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={clearCache}
              disabled={clearing}
              className="gap-1.5"
            >
              <Trash2 className="size-3.5" />
              {clearing ? "Clearing…" : "Clear cache"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
